import { format } from 'date-fns';
import { utils, writeFile } from 'xlsx';
import { processChartData } from '../utils/chartDataProcessors';

/**
 * @returns {Object} Export handlers and utilities
 */
export const useDataExporter = () => {
  const sanitizeSheetName = (name) => {
    // excel doesn't allow: : \ / ? * [ ]
    return name.replace(/[:/?*[\]\\]/g, '').substring(0, 31);
  };

  /**
   * Export data to Excel or CSV
   * @param {string} type - 'excel' or 'csv'
   * @param {Object} options - Export options
   * @param {Array} options.data - Data to export
   * @param {Array} [options.headers] - Column headers
   * @param {string} [options.filename] - Base filename without extension
   * @param {string} [options.sheetName] - Excel sheet name
   * @param {Function} [options.preprocessor] - Optional data preprocessor
   * @param {Function} [options.onError] - Error callback
   */
  const exportData = async (type, options) => {
    const {
      data,
      headers = Object.keys(data[0] || {}),
      filename = `export_${format(new Date(), 'yyyy-MM-dd')}`,
      sheetName = 'Data',
      preprocessor = (d) => d,
      onError,
    } = options;

    if (!data?.length) {
      console.error('No data to export');
      if (onError) onError(new Error('No data to export'), type);
      return;
    }

    try {
      const processedData = preprocessor(data);

      if (type === 'excel') {
        exportExcel(processedData, headers, filename, sheetName);
      } else if (type === 'csv') {
        exportCSV(processedData, headers, filename);
      }
    } catch (error) {
      console.error(`Export ${type} failed:`, error);
      if (onError) onError(error, type);
      throw error;
    }
  };

  /**
   * Export data to Excel
   * @param {Array} data - Processed data
   * @param {Array} headers - Column headers
   * @param {string} filename - Output filename (without extension)
   * @param {string} sheetName - Excel sheet name
   */
  const exportExcel = (data, headers, filename, sheetName) => {
    let worksheet;

    const hasUnits = headers.some((h) => h.includes('(kWh'));

    if (hasUnits) {
      const headerRow = headers.map((h) => h.split(' (kWh')[0]);
      const dataRows = data.map((row) =>
        headerRow.map((key) => {
          const value = row[key];
          if (value === null || value === undefined) return '';
          return value;
        })
      );

      const aoa = [headers, ...dataRows];
      worksheet = utils.aoa_to_sheet(aoa);
    } else {
      worksheet = utils.json_to_sheet(data);

      if (data[0]) {
        const colWidths = Array(Object.keys(data[0]).length).fill({ wch: 15 });
        if (colWidths.length > 0) {
          colWidths[0] = { wch: 20 };
          worksheet['!cols'] = colWidths;
        }
      }
    }

    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, sanitizeSheetName(sheetName));

    writeFile(workbook, `${filename}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  /**
   * Export data to CSV
   * @param {Array} data - Processed data
   * @param {Array} headers - Column headers
   * @param {string} filename - Output filename (without extension)
   */
  const exportCSV = (data, headers, filename) => {
    const keys = headers.map((header) => header.split(' (kWh')[0]);

    const csvContent = [
      headers.join(';'),
      ...data.map((row) =>
        keys
          .map((key) => {
            const value = row[key];

            if (value === null || value === undefined || value === '')
              return '';

            if (typeof value === 'number') {
              return value.toString().replace(/\./g, ',');
            }

            const strValue = String(value);
            if (
              strValue.includes(';') ||
              strValue.includes('"') ||
              strValue.includes('\n')
            ) {
              return `"${strValue.replace(/"/g, '""')}"`;
            }
            return strValue;
          })
          .join(';')
      ),
    ].join('\n');

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   * Export device-specific data to Excel or CSV
   * @param {string} type - 'excel' or 'csv'
   * @param {Object} options - Device export options
   * @param {Array} options.data - Device data to export
   * @param {string} [options.deviceName] - Device name for filename
   * @param {string} [options.deviceType] - Device type for sheet name
   * @param {string} [options.unit] - Data unit
   * @param {Date|string} [options.startDate] - Start date for range
   * @param {Date|string} [options.endDate] - End date for range
   * @param {Function} [options.onError] - Error callback
   */
  const exportDeviceData = (type, options) => {
    try {
      const {
        data,
        deviceName,
        deviceType,
        unit,
        startDate,
        endDate,
        onError,
      } = options;

      if (!data?.length) {
        console.error('No data to export');
        if (onError) onError(new Error('No data to export'), type);
        return;
      }

      const startDateStr = format(new Date(startDate), 'yyyy-MM-dd');
      const endDateStr = format(new Date(endDate), 'yyyy-MM-dd');
      const filename = `${
        deviceName || 'device'
      }_${startDateStr}_to_${endDateStr}`;

      // Process the data using our utility
      const { data: processedData, headers } = processChartData({
        data,
        type: deviceType,
        unit,
      });

      exportData(type, {
        data: processedData,
        headers,
        filename,
        sheetName:
          deviceType?.charAt(0).toUpperCase() + deviceType?.slice(1) || 'Data',
        onError,
      });
    } catch (error) {
      console.error(`Export ${type} failed:`, error);
      if (options.onError) options.onError(error, type);
    }
  };

  /**
   * Generic export handler that can be used for both Excel and CSV exports
   * @param {string} type - 'excel' or 'csv'
   * @param {Object} options - Export options or processing function
   * @param {Function} [setFeedback] - Optional feedback state setter for errors
   */
  const handleExport = (type, options, setFeedback) => {
    try {
      if (typeof options === 'function') {
        const processedOptions = options();
        exportData(type, processedOptions);
      } else {
        exportData(type, options);
      }
    } catch (error) {
      console.error(`Error exporting ${type}:`, error);
      if (setFeedback) {
        setFeedback({
          show: true,
          message: `Failed to export ${type} file. Please try again.`,
          type: 'error',
          title: 'Error',
        });
      }
    }
  };

  return { exportData, exportDeviceData, handleExport };
};
